/**
 * The browser's half of a company's lifecycle: taking its data out, clearing
 * its book, and scheduling (or cancelling) its deletion.
 *
 * Every decision is the database's. The browser shows what the server
 * counted, collects the confirmation — the company name, a fresh sign-in, the
 * export or an explicit "no export" — and passes it on; it never deletes a
 * row itself, never computes what a reset would remove, and never decides that
 * a confirmation is good enough.
 */
import { supabase } from '../lib/supabaseClient';
import { ApiError, apiDownload } from '../lib/api';

export interface FreshStartPreview {
    operation_id: string;
    expires_at: string;
    company_name: string;
    counts: Record<string, number>;
    delete: Record<string, number>;
    keep: Record<string, number>;
    recent_export: { export_id: string; finished_at: string } | null;
    recently_signed_in: boolean;
}

export interface FreshStartResult {
    operation_id: string;
    deleted: Record<string, number>;
    promises: number;
    export_id: string | null;
    export_skipped: boolean;
    replayed: boolean;
}

export interface PausedWorkspace {
    workspace_id: string;
    name: string;
    slug: string;
    status: 'suspended' | 'deletion_pending' | 'deleting';
    deletion_requested_at: string | null;
    deletion_scheduled_for: string | null;
    is_owner: boolean;
}

/** A refusal, in words, and what the screen should do about it. */
export class LifecycleError extends Error {
    constructor(readonly code: string, message: string) {
        super(message);
        this.name = 'LifecycleError';
    }
}

const MESSAGES: Record<string, string> = {
    reauthentication_required: 'For your security, enter your password again to confirm.',
    preview_stale: 'The company’s data changed since you opened this. Review the new numbers and confirm again.',
    preview_expired: 'This confirmation has expired. Review the numbers and confirm again.',
    preview_missing: 'This confirmation is no longer available. Review the numbers and confirm again.',
    export_required: 'Download an export first, or tick the box to continue without one.',
    sync_in_progress: 'A sync of the data source is running. Wait for it to finish, then try again.',
    export_in_progress: 'An export is still downloading. Wait for it to finish, then try again.',
    already_scheduled: 'Deletion of this company is already scheduled.',
    duplicate_request: 'That request was already made. Refresh the page to see where things stand.',
    not_active: 'This company is not in ordinary use, so this cannot be done now.',
    purge_in_progress: 'Deletion has already begun and can no longer be cancelled.',
    not_pending: 'This company is not scheduled for deletion.',
};

/** Supabase's error for an RPC, turned into something a person can act on. */
export function lifecycleError(error: { code?: string; hint?: string | null; message?: string } | null): LifecycleError {
    const hint = error?.hint ?? '';
    if (hint && MESSAGES[hint]) return new LifecycleError(hint, MESSAGES[hint]);
    if (error?.code === '42501') {
        return new LifecycleError('forbidden', 'You do not have permission to do that in this company.');
    }
    if (error?.code === '23514') {
        return new LifecycleError('name_mismatch', 'The company name does not match. Type it exactly as shown.');
    }
    return new LifecycleError('failed', 'That did not work. Nothing was changed. Try again in a moment.');
}

async function rpc<T>(name: string, args: Record<string, unknown>): Promise<T> {
    const { data, error } = await supabase().rpc(name, args);
    if (error) throw lifecycleError(error);
    return data as T;
}

export const previewFreshStart = (workspaceId: string) =>
    rpc<FreshStartPreview>('fresh_start_preview', { p_workspace: workspaceId });

export const applyFreshStart = (
    workspaceId: string, operationId: string, confirmName: string, exportId: string | null, skipExport: boolean,
) => rpc<FreshStartResult>('fresh_start_apply', {
    p_workspace: workspaceId, p_operation: operationId, p_confirm_name: confirmName,
    p_export: exportId, p_skip_export: skipExport,
});

export const requestDeletion = (
    workspaceId: string, requestId: string, confirmName: string, exportId: string | null, skipExport: boolean,
) => rpc<{ operation_id: string; status: string; scheduled_for: string; replayed: boolean }>('request_workspace_deletion', {
    p_workspace: workspaceId, p_request: requestId, p_confirm_name: confirmName,
    p_export: exportId, p_skip_export: skipExport,
});

export const cancelDeletion = (workspaceId: string) =>
    rpc<{ status: string; changed: boolean }>('cancel_workspace_deletion', { p_workspace: workspaceId });

export async function loadPausedWorkspaces(): Promise<PausedWorkspace[]> {
    const { data, error } = await supabase().rpc('my_paused_workspaces');
    if (error) return [];
    return (data ?? []) as PausedWorkspace[];
}

/**
 * Prove it is still you: sign in again with the password. The new session's
 * token carries a fresh sign-in time, which is what the database checks.
 */
export async function reauthenticate(email: string, password: string): Promise<void> {
    const { error } = await supabase().auth.signInWithPassword({ email, password });
    if (error) throw new LifecycleError('wrong_password', 'That password is not right. Try again.');
}

/**
 * Download the Company Data Export and hand it to the browser to save.
 * Returns the export's id, which a Fresh Start or deletion can cite.
 */
export async function downloadCompanyExport(workspaceId: string): Promise<{ exportId: string | null; fileName: string }> {
    const requestId = crypto.randomUUID();
    const { blob, fileName, exportId } = await apiDownload('/api/workspace/export', { workspaceId, requestId });
    const name = fileName ?? 'company-export.zip';
    const url = URL.createObjectURL(blob);
    try {
        const link = document.createElement('a');
        link.href = url;
        link.download = name;
        link.rel = 'noopener';
        document.body.appendChild(link);
        link.click();
        link.remove();
    } finally {
        // Long enough for the browser to start the save.
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
    }
    return { exportId, fileName: name };
}

export const exportErrorMessage = (error: unknown): string =>
    error instanceof ApiError ? error.message : 'The export could not be downloaded. Try again.';

/** A date and time in the reader's own zone, unambiguous about the day. */
export const formatWhen = (iso: string | null): string => {
    if (!iso) return '';
    const date = new Date(iso);
    return Number.isNaN(date.getTime())
        ? ''
        : date.toLocaleString(undefined, { dateStyle: 'full', timeStyle: 'short' });
};
